/**
 * CRÉDITOS (1 crédito = 10,000 tokens). Fuente única para api, worker y web.
 *
 * El libro mayor (eko27) y todo el cobro siguen en TOKENS; el crédito es solo la
 * unidad que se le muestra al cliente. Este archivo trae:
 *   - la conversión y el formato (mismos vectores que portales y bi:
 *     `docs/creditos-vectores.json` de ecosoft-portales);
 *   - el precio de la recarga a la medida (kit_custom) por tramos, solo como VISTA
 *     PREVIA: el precio real lo calcula eko27;
 *   - los promedios de consumo medidos en producción y el factor (×3 por defecto)
 *     para decir "≈ N créditos por …" sin prometer una cifra fija;
 *   - los valores de respaldo con fecha (cuotas del 1-nov-2026) por si eko27 no responde.
 */

export const TOKENS_POR_CREDITO = 10_000;

/** Corte comercial: las cuotas nuevas rigen desde el inicio del mes UTC del libro mayor. */
export const CREDITOS_VIGENCIA_CUOTAS_ISO = '2026-11-01T00:00:00Z';

export type ModoFormatoCreditos = 'saldo' | 'desglose';

function sane(tokens: number | null | undefined): number {
  const n = Number(tokens);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** tokens → créditos (número con decimales; para cálculos, no para pintar). */
export function aCreditos(tokens: number | null | undefined): number {
  return sane(tokens) / TOKENS_POR_CREDITO;
}

/** créditos → tokens (entero). */
export function aTokens(creditos: number | null | undefined): number {
  const n = Number(creditos);
  return Number.isFinite(n) && n > 0 ? Math.round(n * TOKENS_POR_CREDITO) : 0;
}

const nf0 = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 });
const nf2 = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/**
 * 'saldo': entero, hacia abajo (disponible, bolsa, recargas: no se promete saldo que no hay).
 * 'desglose': hasta 2 decimales sin ceros de sobra; >0 y <0.005 → "< 0.01".
 */
export function formatCreditos(tokens: number | null | undefined, modo: ModoFormatoCreditos = 'saldo'): string {
  const t = sane(tokens);
  if (t === 0) return '0';
  if (modo === 'saldo') return nf0.format(Math.floor(t / TOKENS_POR_CREDITO));
  const cents = Math.round(t / 100); // tokens/10,000 × 100
  if (cents === 0) return '< 0.01';
  return nf2.format(cents / 100);
}

/** "N créditos" ("1 crédito" solo si es exactamente 1). */
export function etiquetaCreditos(tokens: number | null | undefined, modo: ModoFormatoCreditos = 'saldo'): string {
  const t = sane(tokens);
  const exactoUno = modo === 'saldo' ? Math.floor(t / TOKENS_POR_CREDITO) === 1 : Math.round(t / 100) === 100;
  return `${formatCreditos(t, modo)} ${exactoUno ? 'crédito' : 'créditos'}`;
}

/** Tokens siempre enteros y exactos ("4,512,300"). */
export function formatTokens(tokens: number | null | undefined): string {
  return nf0.format(Math.round(sane(tokens)));
}

// ── Recarga a la medida (kit_custom): VISTA PREVIA; el precio real lo fija eko27 ──

export interface TramoPanteon {
  desde: number;
  hasta: number;
  precio_credito: number;
}

export interface ConfigPanteon {
  id: string;
  nombre: string;
  min: number;
  max: number;
  paso: number;
  tramos: TramoPanteon[];
}

export const PANTEON_DEFAULT: ConfigPanteon = {
  id: 'kit_custom',
  nombre: 'Recarga a la medida',
  min: 100,
  max: 20_000,
  paso: 50,
  tramos: [
    { desde: 100, hasta: 999, precio_credito: 1.5 },
    { desde: 1000, hasta: 2499, precio_credito: 1.39 },
    { desde: 2500, hasta: 4999, precio_credito: 1.28 },
    { desde: 5000, hasta: 20_000, precio_credito: 1.2 },
  ],
};

/** ¿Cantidad válida para la recarga a la medida? (entero, múltiplo del paso, dentro del rango). */
export function panteonValido(creditos: number, cfg: ConfigPanteon = PANTEON_DEFAULT): boolean {
  return (
    Number.isInteger(creditos) &&
    creditos >= cfg.min &&
    creditos <= cfg.max &&
    creditos % cfg.paso === 0
  );
}

/** Precio por crédito del tramo que le toca a `creditos` (null si no cae en ninguno). */
export function precioCreditoPanteon(creditos: number, cfg: ConfigPanteon = PANTEON_DEFAULT): number | null {
  const t = cfg.tramos.find((x) => creditos >= x.desde && creditos <= x.hasta);
  return t ? t.precio_credito : null;
}

/** Total en pesos (IVA incluido), redondeado al peso. null si la cantidad no es válida. */
export function precioPanteon(creditos: number, cfg: ConfigPanteon = PANTEON_DEFAULT): number | null {
  if (!panteonValido(creditos, cfg)) return null;
  const pc = precioCreditoPanteon(creditos, cfg);
  return pc == null ? null : Math.round(creditos * pc);
}

/** Si hay un paquete fijo más barato o igual con ≥ créditos, se lo sugiere. */
export function sugerirPaquete(
  creditos: number,
  total: number,
  paquetes: Array<{ id: string; label: string; creditos: number; amount: number | null }>,
): { id: string; label: string; creditos: number; amount: number } | null {
  let best: { id: string; label: string; creditos: number; amount: number } | null = null;
  for (const p of paquetes) {
    if (p.amount == null || p.creditos < creditos || p.amount > total) continue;
    if (!best || p.amount < best.amount) best = { id: p.id, label: p.label, creditos: p.creditos, amount: p.amount };
  }
  return best;
}

// ── Factores y promedios ─────────────────────────────────────────────────────

export type FactorClave =
  | 'default'
  | 'catalogos'
  | 'ia_completa'
  | 'apu'
  | 'precio_mercado'
  | 'chat'
  | 'procedimiento'
  | 'junta';

export type Factores = Record<FactorClave, number>;

export const FACTORES_DEFAULT: Factores = {
  default: 3,
  catalogos: 3,
  ia_completa: 3,
  apu: 3,
  precio_mercado: 3,
  chat: 3,
  procedimiento: 3,
  junta: 3,
};

export function normalizarFactores(raw: Partial<Record<string, unknown>> | null | undefined): Factores {
  const out: Factores = { ...FACTORES_DEFAULT };
  if (!raw) return out;
  const base = Number(raw.default);
  if (Number.isFinite(base) && base > 0) {
    for (const k of Object.keys(out) as FactorClave[]) out[k] = base;
  }
  for (const k of Object.keys(out) as FactorClave[]) {
    const v = Number(raw[k]);
    if (Number.isFinite(v) && v > 0) out[k] = v;
  }
  return out;
}

export interface Promedio {
  id: string;
  label: string;
  unidad: string;
  factor_clave: FactorClave;
  /** Tokens reales medidos por unidad (promedio, doc 17 §9). */
  tokens_reales: number;
  /** tokens_reales × factor. */
  tokens: number;
  /** Créditos exactos por unidad. */
  creditos: number;
  /** Créditos redondeados a un escalón fácil de leer. */
  creditos_aprox: number;
  texto: string;
}

/** Promedios medidos en producción (respaldo del 25-sep-2026), tokens REALES por unidad. */
export const PROMEDIOS_BASE: Array<{
  id: string;
  label: string;
  unidad: string;
  factor_clave: FactorClave;
  tokens_reales: number;
}> = [
  { id: 'catalogos', label: 'Cotizar con catálogos', unidad: 'concepto', factor_clave: 'catalogos', tokens_reales: 690 },
  { id: 'ia_completa', label: 'Cotizar con IA completa', unidad: 'concepto', factor_clave: 'ia_completa', tokens_reales: 1_127 },
  { id: 'apu', label: 'APU sintético', unidad: 'APU', factor_clave: 'apu', tokens_reales: 23_186 },
  { id: 'procedimiento', label: 'Procedimiento constructivo', unidad: 'procedimiento', factor_clave: 'procedimiento', tokens_reales: 18_085 },
  { id: 'precio_mercado', label: 'Precio de mercado', unidad: 'insumo', factor_clave: 'precio_mercado', tokens_reales: 3_106 },
  { id: 'chat', label: 'Chat del proyecto', unidad: 'mensaje', factor_clave: 'chat', tokens_reales: 15_109 },
  { id: 'resumen', label: 'Resumen de obra', unidad: 'resumen', factor_clave: 'default', tokens_reales: 13_212 },
  { id: 'planos', label: 'Lectura de planos e imágenes', unidad: 'página', factor_clave: 'ia_completa', tokens_reales: 3_830 },
  { id: 'junta', label: 'Junta / RFI', unidad: 'junta', factor_clave: 'junta', tokens_reales: 207_629 },
  { id: 'indexar', label: 'Indexar documentos', unidad: 'documento', factor_clave: 'default', tokens_reales: 11_169 },
];

/** Redondea un promedio de créditos a un escalón legible (0.1, 0.5, 1, 10). */
export function redondearPromedio(cr: number): number {
  if (!(cr > 0)) return 0;
  if (cr < 1) return Math.max(0.1, Math.round(cr * 10) / 10);
  if (cr < 10) return Math.round(cr * 2) / 2;
  if (cr < 100) return Math.round(cr);
  return Math.round(cr / 10) * 10;
}

const nfAprox = new Intl.NumberFormat('es-MX', { maximumFractionDigits: 1 });

export function textoPromedio(aprox: number, unidad: string): string {
  return `≈ ${nfAprox.format(aprox)} ${aprox === 1 ? 'crédito' : 'créditos'} por ${unidad} (promedio)`;
}

/** Promedios × factor. Respaldo local cuando eko27 no manda `promedios`. */
export function calcularPromedios(factores: Factores = FACTORES_DEFAULT): Promedio[] {
  return PROMEDIOS_BASE.map((p) => {
    const factor = factores[p.factor_clave] ?? factores.default;
    const tokens = Math.round(p.tokens_reales * factor);
    const creditos = aCreditos(tokens);
    const creditos_aprox = redondearPromedio(creditos);
    return { ...p, tokens, creditos, creditos_aprox, texto: textoPromedio(creditos_aprox, p.unidad) };
  });
}

// ── Respaldo con fecha de las cuotas (si eko27 no responde) ──────────────────

export interface CuotasPlan {
  tokens_month: number;
  tokens_per_extra_seat?: number;
}

/** Cuotas vigentes hasta el 31-oct-2026 (fin del mes UTC). */
export const CUOTAS_ANTERIORES: Record<'start' | 'pro' | 'teams' | 'vitruvio', CuotasPlan> = {
  start: { tokens_month: 2_000_000 },
  pro: { tokens_month: 5_000_000 },
  teams: { tokens_month: 15_000_000, tokens_per_extra_seat: 5_000_000 },
  vitruvio: { tokens_month: 50_000_000 },
};

/** Cuotas desde el 1-nov-2026 (450 / 1,000 / 1,000 por plaza / 10,000 créditos). */
export const CUOTAS_NUEVAS: Record<'start' | 'pro' | 'teams' | 'vitruvio', CuotasPlan> = {
  start: { tokens_month: 4_500_000 },
  pro: { tokens_month: 10_000_000 },
  teams: { tokens_month: 30_000_000, tokens_per_extra_seat: 10_000_000 },
  vitruvio: { tokens_month: 100_000_000 },
};

/** Recarga base (Recarga 400, `kit_3m`): 400 créditos desde el 1-nov; 300 antes. */
export const KIT_3M_TOKENS_ANTES = 3_000_000;
export const KIT_3M_TOKENS_DESPUES = 4_000_000;

export function cuotasVigentes(now: number = Date.now()): typeof CUOTAS_NUEVAS {
  return now >= Date.parse(CREDITOS_VIGENCIA_CUOTAS_ISO) ? CUOTAS_NUEVAS : CUOTAS_ANTERIORES;
}

export function kit3mTokensVigentes(now: number = Date.now()): number {
  return now >= Date.parse(CREDITOS_VIGENCIA_CUOTAS_ISO) ? KIT_3M_TOKENS_DESPUES : KIT_3M_TOKENS_ANTES;
}
